<template>
  <div class="b-contract__card-data-wrap">
    <a @click="goBack()" class="b-contract__card-data-back mt-2">
      <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="back"></dynamic-icon>
    </a>
    <div class="b-contract__card-data-main">
      <div class="mb-4" v-if="error">
        <server-error :error="error"></server-error>
      </div>
      <div class="row">
        <div class="col-12">
          <div class="row px-0">
            <div class="col-7">
              <div class="b-contract__card-data-info-about-wrap mb-5">
                <div class="b-contract__card-data-title-wrap">
                  <h1 class="mb-1">{{$tc('CONTENT.contract.contract', 1)}} № {{contract.number}}
                    <div class="b-contract__card-data-title-inner">
                      {{$t('CONTENT.from')}} {{contract.date | dateFormatted}}
                      <span class="b-contract__status">({{ getContractStatus(contract.status) }})</span>

                      <button
                        v-if="isContractRawEditing && (!isInClosedPeriod || contract.status === 1)"
                        class="b-btn b-btn_border_none b-btn_type_icon_accent_color"
                        @click="openCardDataPopupClick">
                        <dynamic-icon class="b-btn__icon" v-tooltip="$t('CONTENT.hints.editCardData')" icon="edit"/>
                      </button>
                      <button
                        v-if="isContractChangeConditions && !isContractEventState"
                        class="b-btn b-btn_border_none b-btn_type_icon_accent_color"
                        @click="openCardDataEventsPopupClick">
                        <dynamic-icon class="b-btn__icon" v-tooltip="$t('CONTENT.hints.editCardData')" icon="edit"/>
                      </button>
                      <button
                        v-if="isContractRawSimplifiedEditing || (isContractRawEditing && isInClosedPeriod && contract.status !== 1)"
                        class="b-btn b-btn_border_none b-btn_type_icon_accent_color"
                        @click="openCardDataSimplifiedPopupClick">
                        <dynamic-icon class="b-btn__icon" v-tooltip="$t('CONTENT.hints.editCardData')" icon="edit"/>
                      </button>
                    </div>
                  </h1>
                </div>
                <div class="b-edit-card__info-about-row mb-2">
                  <div class="col-8 px-0 b-contract_row_flex b-contract__secondary-text">
                    <div v-if="contract.idInDB" class="mr-3">id: {{contract.idInDB}}</div>
                    <div v-if="contract.externalId">{{$t('CONTENT.external_id_small')}}: {{contract.externalId}}</div>
                  </div>
                </div>
                <div class="b-edit-card__info-about-row mb-2">
                  <div class="col-8 px-0 b-contract_row_flex b-contract__secondary-text">
                    <div
                      v-if="contract.responsible"
                      class
                    >{{$t('CONTENT.contract.initiator')}}: {{contract.responsible}}</div>
                  </div>
                </div>
                <div v-if="contract.externalLink" class="b-edit-card__info-about-row mb-2">
                  <div class="col-8 px-0 b-contract_row_flex b-contract__secondary-text">
                    {{$t('CONTENT.contract.externalLink')}}: &nbsp;<a :href="contract.externalLink" target="_blank" class="mr-3">{{contract.externalLinkName}}</a>
                  </div>
                </div>
                <div v-if="contract.prolongationType" class="b-edit-card__info-about-row mb-2">
                  <div class="col-8 px-0 b-contract_row_flex b-contract__secondary-text">
                    {{$t('CONTENT.contract.prolongationType')}}: {{contract.prolongationType}}
                  </div>
                </div>
                <div class="col-8 px-0 b-contract__card-data-info-about mb-2">
                  <div class="b-contract__card-data-info-about-row-name mb-1">{{$t('CONTENT.contract.lessee')}}:</div>
                  <div class="b-contract__card-data-info-about-row-value">
                    {{currentLessee}}
                    <div
                      class="b-counterparty-content__info-checked-item ml-1"
                      v-if="checkIsCounterpartyIntragroup(contract.lesseeCounterpartyId)">
                      <dynamic-icon class="mr-2" icon="check"/>
                    </div>
                  </div>
                </div>
                <div
                  class="col-8 px-0 b-edit-card__info-about mb-2"
                  v-if="currentLessors.length > 0">
                  <div class="b-contract__card-data-info-about-row-name mb-1">{{$t('CONTENT.contract.lessor')}}:</div>
                  <template v-for="item in currentLessors">
                    <div class="b-contract__card-data-info-about-row-value">
                      {{item.name}}
                      <div
                        class="b-counterparty-content__info-checked-item ml-1"
                        v-if="checkIsCounterpartyLessorIntragroup(item.id)">
                        <dynamic-icon class="mr-2" icon="check"/>
                      </div>
                    </div>
                  </template>
                </div>
                <div class="col-8 px-0 b-edit-card__info-about" v-if="contract.commentary">
                  <div class="b-contract__card-data-info-about-row-name mb-1">{{$t('CONTENT.comment')}}:</div>
                  <div class="b-contract__card-data-info-about-row-value">{{contract.commentary}}</div>
                </div>
              </div>
            </div>
            <div class="col-5">
              <div class="b-contract__edit-btns mt-2 mr-2">
                <template v-if="!this.isContractRawEditing && !this.isContractRawSimplifiedEditing && !this.isContractChangeConditions">
                  <button
                    v-if="isLastVersion && !isNoEvents && !isReadOnlyMode"
                    data-cy="edit-button"
                    class="b-btn b-btn__icon_color_main-text"
                    @click="onClickEditContractSimplified"
                    v-tooltip.left="$t('CONTENT.hints.editContractHint')">
                      <dynamic-icon icon="edit" class="b-btn__icon"/>
                      {{$t('CONTENT.contract.edit')}}
                  </button>
                  <button
                    v-if="isLastVersion && isNoEvents && !isReadOnlyMode"
                    data-cy="edit-button"
                    class="b-btn b-btn__icon_color_main-text"
                    @click="onClickEditContract"
                    v-tooltip.left="$t('CONTENT.hints.editContractHint')">
                      <dynamic-icon icon="edit" class="b-btn__icon"/>
                      {{$t('CONTENT.contract.edit')}}
                  </button>
                  <button
                    v-if="isLastVersion && !isReadOnlyMode"
                    type="button"
                    class="b-btn b-btn_type_primary ml-6"
                    @click="onClickChangeCondition"
                    v-tooltip.left="$t('CONTENT.hints.changeConditionHint')"
                  >{{$t('CONTENT.contract.change_condition')}}</button>
                </template>
                <template v-else>
                  <button
                    v-if="!isContractEventState"
                    type="button"
                    class="b-btn b-btn_type_empty ml-6"
                    @click="cancelContractChange">
                      {{$t('CONTENT.cancel')}}
                  </button>
                  <button
                    v-if="!isContractEventState"
                    type="button"
                    class="b-btn b-btn_type_primary ml-6"
                    @click="saveContract">
                      {{$t('CONTENT.save')}}
                  </button>
                </template>
              </div>
              <div v-if="!isShowErrors">
                <div v-if="useRole === 'true'">
                  <div v-if="role === 'Admin'">
                    <div v-if="contract.status === 2 && isLastVersion" class="b-contract__edit-btns mt-2 mr-2">
                      <template v-if="!this.isContractRawEditing && !this.isContractRawSimplifiedEditing && !this.isContractChangeConditions">
                        <button
                          type="button"
                          class="b-btn b-btn_type_primary ml-6"
                          @click="castContractStatus(1)"
                          v-tooltip.left="$t('CONTENT.hints.changeConditionHint')"
                        >{{$t('CONTENT.contract.send_to_edit')}}}</button>
                        <button
                          type="button"
                          class="b-btn b-btn_type_primary ml-6"
                          @click="castContractStatus(5)"
                          v-tooltip.left="$t('CONTENT.hints.changeConditionHint')"
                        >{{$t('CONTENT.contract.apply')}}</button>
                      </template>
                    </div>
                    <div v-else-if="contract.status === 4 && isLastVersion" class="b-contract__edit-btns mt-2 mr-2">
                      <template v-if="!this.isContractRawEditing && !this.isContractRawSimplifiedEditing && !this.isContractChangeConditions">
                        <button
                          type="button"
                          class="b-btn b-btn_type_primary ml-6"
                          @click="castContractStatus(3)"
                          v-tooltip.left="$t('CONTENT.hints.changeConditionHint')"
                        >{{$t('CONTENT.contract.send_to_edit')}}</button>
                        <button
                          type="button"
                          class="b-btn b-btn_type_primary ml-6"
                          @click="castContractStatus(5)"
                          v-tooltip.left="$t('CONTENT.hints.changeConditionHint')"
                        >{{$t('CONTENT.contract.apply')}}</button>
                      </template>
                    </div>
                    <div v-if="contract.status === 1 && isLastVersion" class="b-contract__edit-btns mt-2 mr-2">
                      <template v-if="!this.isContractRawEditing && !this.isContractRawSimplifiedEditing && !this.isContractChangeConditions">
                        <button
                          type="button"
                          class="b-btn b-btn_type_primary ml-6"
                          @click="castContractStatus(2)"
                          v-tooltip.left="$t('CONTENT.hints.changeConditionHint')"
                        >{{$t('CONTENT.contract.send_to_apply')}}</button>
                      </template>
                    </div>
                    <div v-else-if="contract.status === 3 && isLastVersion" class="b-contract__edit-btns mt-2 mr-2">
                      <template v-if="!this.isContractRawEditing && !this.isContractRawSimplifiedEditing && !this.isContractChangeConditions">
                        <button
                          type="button"
                          class="b-btn b-btn_type_primary ml-6"
                          @click="castContractStatus(4)"
                          v-tooltip.left="$t('CONTENT.hints.changeConditionHint')"
                        >{{$t('CONTENT.contract.send_to_apply')}}</button>
                      </template>
                    </div>
                  </div>
                  <div v-else-if="role === 'Operator'">
                    <div v-if="contract.status === 1 && isLastVersion" class="b-contract__edit-btns mt-2 mr-2">
                      <template v-if="!this.isContractRawEditing && !this.isContractRawSimplifiedEditing && !this.isContractChangeConditions">
                        <button
                          type="button"
                          class="b-btn b-btn_type_primary ml-6"
                          @click="castContractStatus(2)"
                          v-tooltip.left="$t('CONTENT.hints.changeConditionHint')"
                        >{{$t('CONTENT.contract.send_to_apply')}}</button>
                      </template>
                    </div>
                    <div v-else-if="contract.status === 3 && isLastVersion" class="b-contract__edit-btns mt-2 mr-2">
                      <template v-if="!this.isContractRawEditing && !this.isContractRawSimplifiedEditing && !this.isContractChangeConditions">
                        <button
                          type="button"
                          class="b-btn b-btn_type_primary ml-6"
                          @click="castContractStatus(4)"
                          v-tooltip.left="$t('CONTENT.hints.changeConditionHint')"
                        >{{$t('CONTENT.contract.send_to_apply')}}</button>
                      </template>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <div v-if="useRole === 'true'">
                    <div v-if="(contract.status === 1 || contract.status === 3) && isLastVersion" class="b-contract__edit-btns mt-2 mr-2">
                      <template v-if="!this.isContractRawEditing && !this.isContractRawSimplifiedEditing && !this.isContractChangeConditions">
                        <button
                          type="button"
                          class="b-btn b-btn_type_primary ml-6"
                          @click="castContractStatus(5)"
                          v-tooltip.left="$t('CONTENT.hints.changeConditionHint')"
                        >{{$t('CONTENT.contract.apply')}}</button>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              <div class="b-contract__card-data-liabilities-wrap mb-4 mr-2">
                <div class="b-contract__card-data-liabilities-item mb-2">{{$t('CONTENT.contract.liabilities_on')}} {{liabilitiesDate}}</div>
                <div class="b-contract__card-data-liabilities-item b-contract__card-data-liabilities-item_type_large">
                  <total-cost-span-widget :contractId="contract.idInDB" :currentVersionId="currentVersionId"/>
                  {{contractSettingsCurrencyCode}}
                </div>
              </div>
            </div>
            <div v-if="!isReadOnlyMode" class="col-12">
              <div class="b-contract__edit-btns">
                <button
                  v-if="deactivateBtnVisible"
                  type="button"
                  class="b-btn"
                  :disabled="deactivateBtnDisable"
                  @click="clickDeactivationEvent">
                  {{$t('CONTENT.events.DeactivateEvent')}}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import dateConverter from "../../../../utils/converters/date.converter";
import ContractAggregateAPI from "@/api/ContractAggregateAPI";

export default {
  props: {
    prevRoute: {
      type: String,
    },
    contract: {
      type: Object
    },
    assetComponents: {
      type: Array
    },
    role: {
      type: String,
    },
    useRole: {
      type: String,
    },
    isReadOnlyMode: {
      type: Boolean,
    },
    isShowErrors: {
      type: Boolean,
    },
  },
  data() {
    return {
      error: null,
      liabilitiesDate: null,
      events: null,
      currentContactId: null,
      deactivateBtnVisible: false,
      contractStatuses: {
        5: `${this.$t('CONTENT.contract_status.status_active')}`,
        4: `${this.$t('CONTENT.contract_status.status_on_approval_modified')}`,
        3: `${this.$t('CONTENT.contract_status.status_modified')}`,
        2: `${this.$t('CONTENT.contract_status.status_on_approval_create')}`,
        1: `${this.$t('CONTENT.contract_status.status_project')}`,
      },
    };
  },
  watch: {
    $route() {
      this.setDeactivateBtnVisibility(this.$route);
    },
  },
  async created() {
    this.setDeactivateBtnVisibility(this.$route);
    await this.getLiabilitiesDate();
    this.setInClosedPeriodState();
    this.$eventRegistryManager.clearCommands();
  },
  computed: {
    ...mapGetters(["getContractState"]),
    ...mapGetters(["getContractStateSimplified"]),
    ...mapGetters(["allCounterparties"]),
    ...mapGetters(["getContractSettings"]),
    ...mapGetters(["getContractConditionsState"]),
    ...mapGetters(["getCurrentContract"]),
    ...mapGetters(["getContractEventState"]),
    ...mapGetters(["contractIsInClosedPeriod"]),
    ...mapGetters("eventRegistryPlugin", ["getDirectoryEvents"]),
    currentVersionId() {
      return this.$route.query.eventId;
    },
    isLastVersion() {
      // is last if not event id
      const eventId = this.$route.query.eventId;
      if (eventId == null) return true;
      // is last if event id equals last element
      const events = this.getDirectoryEvents;
      if (events.length === 0) return false;
      if (events[events.length - 1].idInDB == eventId) return true;
      // else not last
      return false;
    },

    deactivateBtnDisable() {
      return this.isNoEvents || this.getContractSettings.lastClosePeriodDate === "";
    },
    isNoEvents() {
      return this.getDirectoryEvents.length == 1;
    },
    isInClosedPeriod() {
      return this.contractIsInClosedPeriod;
    },
    isContractRawEditing() {
      return this.getContractState;
    },
    isContractRawSimplifiedEditing() {
      return this.getContractStateSimplified;
    },
    isContractChangeConditions() {
      return this.getContractConditionsState;
    },
    isContractEventState() {
      return this.getContractEventState;
    },
    currentLessors() {
      return this.allCounterparties
        .filter(counterparty => this.contract.lessorCounterpartyIds.includes(counterparty.idInDB))
        .map(counterparty => {
          return {
            name: counterparty.displayName,
            id: counterparty.idInDB
          }
        })
    },
    currentLessee() {
      return this.allCounterparties
        .find(counterparty => counterparty .idInDB === this.contract.lesseeCounterpartyId).displayName
    },
    contractSettingsCurrencyCode() {
      return this.getContractSettings.currencyCode;
    }
  },
  methods: {
    getContractStatus(statusId) {
      return this.contractStatuses[statusId] || '';
    },
    async castContractStatus(newStatus) {
      try {
        this.$fileLoadingManager.startLoading();
        const contractId = this.contract.idInDB;
        console.log(this.contract);
        const requestData = { contractId, newStatus };
        const api = new ContractAggregateAPI();
        await api.changeContractStatus(requestData);
        this.contract.status = newStatus;
      } catch (e) {
        this.error = e;
        this.$fileLoadingManager.finishLoading();
      }
      this.$fileLoadingManager.finishLoading();
    },
    setDeactivateBtnVisibility(route) {
      this.deactivateBtnVisible = route.name === 'ContractVersions';
    },
    checkIsCounterpartyIntragroup(counterpartyId) {
      for (let i = 0; i < this.allCounterparties.length; i++) {
        if (this.allCounterparties[i].idInDB === counterpartyId) {
          return this.allCounterparties[i].isIntragroupTurnover;
        }
      }
    },
    checkIsCounterpartyLessorIntragroup(counterpartyId) {
      for (let i = 0; i < this.allCounterparties.length; i++) {
        if (this.allCounterparties[i].idInDB === counterpartyId) {
          return this.allCounterparties[i].isIntragroupTurnover;
        }
      }
    },

    async getCurrencyRate(paymentsParameter, startDate) {
      let encodedStartDate = encodeURIComponent(startDate);
      let currencyRateData = {};

      currencyRateData.onDate = encodedStartDate;
      currencyRateData.type = paymentsParameter.currencyRateParameters.type;
      currencyRateData.currencyCode = paymentsParameter.currencyCode;
      currencyRateData.minValue =
        paymentsParameter.currencyRateParameters.minValue;
      currencyRateData.maxValue =
        paymentsParameter.currencyRateParameters.maxValue;
      currencyRateData.fixedValue =
        paymentsParameter.currencyRateParameters.fixedValue;

      return await this.$store.dispatch("getCurrencyRate", currencyRateData);
    },
    async getLiabilitiesDate() {
      this.events = await this.$eventRegistryManager.loadContractVersions(
        this.contract.idInDB
      );
      let date = this.events[this.events.length - 1].eventDate;


      let convertedDate = dateConverter.convertToDate(date);
      this.liabilitiesDate = this.$d(convertedDate);
    },
    setInClosedPeriodState() {
      const contractDate = dateConverter.convertToDate(this.events[0].createEventDate);
      const closedPeriodDate = dateConverter.convertToDate(this.getContractSettings.lastClosePeriodDate);
      const isInClosedPeriod = contractDate < closedPeriodDate;
      this.$store.commit("SET_CONTRACT_IN_CLOSED_PERIOD_STATE", isInClosedPeriod);
    },
    onClickEditContract() {
      this.$store.commit("SET_CONTRACT_RAW_EDITING_STATE", true);
    },
    onClickEditContractSimplified() {
      this.$store.commit("SET_CONTRACT_RAW_EDITING_SIMPLIFIED_STATE", true);
    },
    onClickChangeCondition() {
      this.$store.commit("SET_CONTRACT_CHANGE_CONDITION_STATE", true);
    },
    clickDeactivationEvent() {
      this.$emit("onClickDeactivationEvent");
    },
    async saveContract() {
      this.$store.commit('CLEAR_RENAME_LESSOR_LIST');
      if (this.isContractRawEditing) {
        this.$emit("onClickSaveEditContract");
      } else if (this.isContractRawSimplifiedEditing) {
        this.$emit("onClickSaveEditContract");
      } else {
        this.$eventRegistryManager.toggleModalToFormDirectoryEvent(true);
      }
    },
    cancelContractChange() {
      this.$store.commit('CLEAR_RENAME_LESSOR_LIST');
      this.$store.commit("eventRegistryPlugin/addIntegrationEventDate", null);
      this.$store.commit("eventRegistryPlugin/addIntegrationEventIdInDb", null);

      if (this.isContractRawEditing) {
        this.$store.commit("SET_CONTRACT_RAW_EDITING_STATE", false);
        this.$emit('cancelEditContract');
        this.$eventBus.$emit('cancelEditContract');
      } else if(this.isContractRawSimplifiedEditing) {
        this.$store.commit("SET_CONTRACT_RAW_EDITING_SIMPLIFIED_STATE", false);
      } else {
        this.$store.commit("SET_CONTRACT_CHANGE_CONDITION_STATE", false);
        this.$store.commit("eventRegistryPlugin/clearIntegrationEventList");
        this.$emit('cancelEditContract');
        this.$eventBus.$emit('cancelEditContract');
        this.$eventRegistryManager.clearCommands();
      }
      this.$store.commit("eventRegistryPlugin/clearIndex");
    },
    openCardDataPopupClick() {
      this.$emit("onClickEditCardData");
    },
    openCardDataEventsPopupClick() {
      this.$emit("onClickEditEventsCardData");
    },
    openCardDataSimplifiedPopupClick() {
      this.$emit("onClickEditCardDataSimplified");
    },
    goBack() {
      this.$router.push({path: this.prevRoute})
    }
  }
};
</script>
